import { mriHandoff } from "@/mri-bridge";
import Vue from "vue";
import VueRouter from "vue-router";
import { getTracker } from "@/utils/mixpanel-tracker";
import { peekApiFailure, peekFrom, recordFrom } from "@/utils/errorContext";
import {
  authLostGuardDecision,
  clearAuthFailure,
  goToLoginAfterExpiryCleanup,
  loginHref,
  noteLoginRedirect,
} from "@/utils/authReturn";
import { getAuthAccessToken } from "@/utils/authSession";
import previewRouter from "./route-preview";
import analyzeRouter from "./route-analyze";
import contractRouter from "./route-contract";
import loginRouter from "./route-login";
import realtorRouter from "./route-realtor";
import reportRouter from "./route-report";
import sciRouter from "./route-sci";
import creditRouter from "./route-credit";
import subscribeRouter from "./route-subscribe";
import withdrawRouter from "./route-withdraw";
import withdrawV2Router from "./route-withdraw-v2";
import seoFeedRouter from "./route-seo";
import guaranteeRouter from "./route-guarantee";
import etcRouter from "./route-etc";
import abRouter from "./route-a-b";
import ticketRouter from "./route-ticket";
import orderRouter from "./route-order";
import eventRouter from "./route-event";
import loanouter from "./route-loan";
import ownerRouter from "./route-owner";
import dobyRouter from "./route-doby";
import insuranceRouter from "./route-insurance";
import userRouter from "./route-user";
import sherlockRouter from "./route-sherlock";
import testingRouter from "./route-testing";
import campaignRouter from "./route-campaign";
import buyRouter from "./route-buy";
import alfredRouter from "./route-alfred";
import { flushBehaviorNow } from "@/utils/behaviorQueue";

Vue.use(VueRouter);

const routes = [
  {
    // 인천 캠페인 종료(NZSC-3218): 단축 URL도 홈으로 redirect (랜딩 미노출)
    path: "/ich",
    redirect: "/",
  },

  //---------------------
  //메뉴
  //---------------------
  {
    path: "/",
    name: "home",
    component: () => import("../views/MainAnalyzeView.vue"),
    redirect: "/analyze",
  },
  {
    path: "/mypage",
    name: "mypage",
    component: () => import("../views/MyPage.vue"),
  },
  {
    path: "/main",
    name: "main",
    component: () => import("../views/MainView.vue"),
  },
  {
    path: "/global",
    name: "global",
    component: () => import("../views/analyze/AnalyzeMainLocaleView.vue"),
  },
  {
    path: "/ione",
    name: "ione",
    component: () => import("../views/ibk/IbkMainView.vue"),
  },

  //---------------------
  //공통
  //---------------------
  {
    path: "/login/error",
    name: "login-error",
    component: () => import("../views/LoginError.vue"),
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("../views/LogoutView.vue"),
  },

  // IBK
  {
    path: "/mypage-ione",
    name: "mypage-ione",
    component: () => import("../views/ibk/IbkMyPage.vue"),
  },

  //---------------------
  //기타
  //---------------------
  ...etcRouter,

  //---------------------
  //seo 피드
  //---------------------
  ...seoFeedRouter,

  //---------------------
  // 내집스캔 보증
  //---------------------
  ...guaranteeRouter,

  //---------------------
  // 분석퍼널
  //---------------------
  ...analyzeRouter,

  //---------------------
  // 미리보기
  //---------------------
  ...previewRouter,

  //---------------------
  // 계약데이터
  //---------------------
  ...contractRouter,

  //---------------------
  // 로그인
  //---------------------
  ...loginRouter,

  //---------------------
  // 공인중개사
  //---------------------
  ...realtorRouter,

  //---------------------
  // 리포트
  //---------------------
  ...reportRouter,

  //---------------------
  // sci
  //---------------------
  ...sciRouter,

  //---------------------
  // credit (개인신용 조회)
  //---------------------
  ...creditRouter,

  //---------------------
  // 구독
  //---------------------
  ...subscribeRouter,

  //---------------------
  // 회원탈퇴
  //---------------------
  ...withdrawRouter,

  //---------------------
  // 회원탈퇴 V2 (새 UI)
  //---------------------
  ...withdrawV2Router,

  //---------------------
  // a-b테스트
  //---------------------
  ...abRouter,

  //---------------------
  // 이용권,쿠폰
  //---------------------
  ...ticketRouter,

  //---------------------
  // 결제
  //---------------------
  ...orderRouter,

  //---------------------
  // 프리미엄 리포트 (잔금일 입력)
  //---------------------
  {
    path: "/premium-report/balance-date",
    name: "PremiumReportBalanceDate",
    component: () => import("../views/buy-report/BalanceScheduleInput.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/premium-report/balance-complete",
    name: "PremiumReportBalanceComplete",
    component: () => import("../views/buy-report/BalanceScheduleComplete.vue"),
    meta: { requiresAuth: true },
  },

  //---------------------
  // 이벤트
  //---------------------
  ...eventRouter,

  //---------------------
  // 전세대출
  //---------------------
  ...loanouter,

  //---------------------
  // 집주인
  //---------------------
  ...ownerRouter,

  //---------------------
  // 도비
  //---------------------
  ...dobyRouter,

  //---------------------
  // 보험
  //---------------------
  ...insuranceRouter,

  //---------------------
  // 회원
  //---------------------
  ...userRouter,

  //---------------------
  // 셜록 (jasanoneclick.com 도메인에서만 사용, naezipscan.com에서는 리다이렉트)
  //---------------------
  ...sherlockRouter,

  //---------------------
  // 캠페인
  //---------------------
  ...campaignRouter,

  //---------------------
  // 매매
  //---------------------
  ...buyRouter,

  //---------------------
  // AI 계약 상담 (alfred, NZSC-3151/3153)
  //---------------------
  ...alfredRouter,

  //---------------------
  // 테스트
  //---------------------
  ...(process.env.NODE_ENV === "development" ? testingRouter : []),
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

let lastRoutePath = null;
let reportEntryPoint = null;

// NZSC-3481: web-core(Next.js)로 컷오버된 라우트 목록 — vercel.json rewrite와 한 세트.
// Vercel rewrite는 풀 페이지 로드에만 적용되므로, 인앱 $router.push 진입은 여기서
// next(false)+location.href 풀 로드로 전환해 rewrite를 태운다(아니면 Vue 원본이 이중 서빙됨).
// ⚠️ 컷오버 게이트 규칙: 라우트를 컷오버하면 rewrite 추가와 함께 이 목록에도 반드시 추가한다.
// 정본 목록 = naezipscan-web-core migration-map.json (status=live)
const WEB_CORE_MIGRATED_ROUTES = new Set([
  // KRE-46 — 로그인 진입 UI만 전환. /analyze/login·Apple callback·로그아웃은 Vue 유지.
  "/login",
  // KRE-46 — NZSC-4225에서 준비한 독립 4화면 컷오버 후보(rewrite와 함께 반영)
  "/buy/direct",
  "/insurance/lotte/legal-001/landing",
  "/home-loan",
  "/cheat/financial/fraud",
  // NZSC-4216 — 정부지원 대출 진단 8경로 묶음 컷오버
  "/loan",
  "/loan/login",
  "/loan/gov-analyze",
  "/loan/gov-analyze-impossible",
  "/loan/gov/possible",
  "/loan/gov/possible/analyze",
  "/loan/gov/impossible",
  "/loan/gov/impossible/analyze",

  // NZSC-4205 — 대출 상담 신·구 경로 묶음 컷오버
  "/loan/relay-chat",
  "/loan/consult/chat",
  "/main",
  "/doby",
  "/doby/landing",
  "/doby/landing-ad",
  "/doby/list",
  "/doby/reviews",
  "/subscribe",
  "/mypage",
  // KRE-128 — 마이페이지 개선 하위 화면(web-core 신규, Vue 원본 없음)
  "/mypage/services",
  "/mypage/reports",
  "/mypage/notifications",
  "/mypage/settings/notification",
  "/mypage/account",
  "/mypage/ticket",
  "/mypage/rental-info",
  // KRE-128 — 계정 관리 번호 등록·변경 PASS(KCB) 본인인증 결과(web-core 신규)
  "/user/verifications/kcb/result/mypage_mobile",
  "/insurance/firstamerican/title-001/landing",
  // NZSC-3901 — 안심 매매 패키지 채팅 상담(web-core 신규, Vue 원본 없음)
  "/insurance/firstamerican/title-001/chat",
  "/insurance/firstamerican/title-001/chat/list",
  // NZSC-4027 — 결제완료 넛지 → 권원보험 범퍼 콘텐츠 페이지(web-core 신규, Vue 원본 없음)
  "/insurance/firstamerican/title-001/bumper",
  // NZSC-4145 — 안심 매매 패키지 가입 내역(GNB 매매보증 탭, web-core 신규, Vue 원본 없음)
  "/insurance/firstamerican/title-001/my",
  // NZSC-4248 — 권원보험 가입 대기 신청 퍼널(web-core 신규, Vue 원본 없음). 계약상태(step-1)·재신청 모달은 Vue
  "/insurance/firstamerican/title-001/waitlist/move-period",
  "/insurance/firstamerican/title-001/waitlist/confirm",
  "/insurance/firstamerican/title-001/waitlist/complete",
  "/insurance/firstamerican/title-001/waitlist/history",
  "/a-b/dateconfirm",
  "/landing/monthly",
  "/tax/danger",
  "/tax/notice",
  "/report/deposit/records",
  "/campaign/seoul",
  "/miso/move",
  "/subscribe/owner-house-complete",
  "/loan/house",
  "/loan/income",
  "/loan/hold",
  "/loan/hold-active",
  "/loan/hold-repay",
  "/loan/property",
  "/loan/age",
  "/loan/condition",
  "/loan/difficult",
  "/analyze",
  "/landing",
  "/guide-summary",
  "/loan/guide/concept",
  "/loan/guide/repayment",
  "/loan/guide/interest",
  "/loan/guide/interest-tip",
  "/loan/guide/limit-tip",
  "/feed/home-frauud",
  "/feed/loan",
  "/feed/insurance",
  "/feed/alarm",
  "/feed/loan-landing",
  "/feed/buy-conflicts",
  "/fraud/landing",
  "/report/compare",
  "/report/compare/list",
  "/credit/indiv/list",
  "/report/contract/date",
  "/report/contract/collateral-cancel",
  "/report/contract/product",
  "/analyze/faq",
  "/analyze/faq-area",
  "/analyze/type",
  "/analyze/period",
  "/analyze/period-detail",
  "/analyze/section",
  "/analyze/area",
  "/analyze/freebase",
  "/analyze/freelogin",
  "/analyze/price",
  "/analyze/date",
  "/analyze/date/living",
  "/analyze/address",
  "/analyze/search",
  // NZSC-4010 — 매매 신청 퍼널 잔금일 입력(web-core 신규, Vue 원본 없음)
  "/analyze/balance-date",
  // 저위험 1차 배치 (NZSC-3808 컷오버)
  "/subscribe/amount",
  "/login/error",
  "/subscribe/card-change-complete",
  "/report/deposit/enter",
  "/withdraw-v2/complete",
  // 저위험 2차 배치 (NZSC-3817 컷오버)
  "/report-preview",
  "/a-b/prevention",
  "/guarantee/information",
  "/guarantee/complete",
  // 저위험 3차 배치 (NZSC-3819 컷오버) — 이관 78/257 = 30.4%
  "/analyze/error",
  "/analyze/extension",
  "/analyze/realtor-price",
  "/analyze/realtor-price-reason",
  "/analyze/faqwrite",
  "/analyze/cash",
  "/creditinfo/error",
  "/analyze/apple/login/cancel",
]);

// NZSC-4216: 같은 정부지원 화면의 끝 슬래시 별칭도 SPA 진입 시 Next로 넘긴다.
// canonical 목록에서 파생하며 대출 가이드·상담 등 다른 묶음은 변경하지 않는다.
// NZSC-4226: 광고 링크가 끝 슬래시로 들어오는 이관 완료 랜딩 4경로도 같은 별칭 처리
// (/landing/monthly/ 등 — 30일 약 7,000pv가 Vue로 서빙되던 누수). vercel.json rewrite와 한 세트.
const WEB_CORE_TRAILING_SLASH_ROUTES = new Set([
  "/login",
  // KRE-46: 새 컷오버 후보도 광고·직접 링크의 끝 슬래시를 같은 Next 화면으로 보낸다.
  "/buy/direct",
  "/insurance/lotte/legal-001/landing",
  "/home-loan",
  "/cheat/financial/fraud",
  "/a-b/dateconfirm",
  "/landing/monthly",
  "/doby/landing-ad",
  "/insurance/firstamerican/title-001/landing",
]);
const WEB_CORE_LOAN_GOV_ALIASES = new Set(
  [...WEB_CORE_MIGRATED_ROUTES]
    .filter(
      (path) =>
        path === "/loan" ||
        path === "/loan/login" ||
        path.startsWith("/loan/gov") ||
        WEB_CORE_TRAILING_SLASH_ROUTES.has(path)
    )
    .map((path) => `${path}/`)
);

// 캐싱
let cachedDomainCheck = null;
function getDomainInfo() {
  if (cachedDomainCheck === null) {
    const currentHost = window.location.hostname;
    // endsWith를 사용하여 더 안전하게 체크 (예: fakenaezipscan.com 방지)
    cachedDomainCheck = {
      isNaezipscanDomain:
        currentHost === "naezipscan.com" || currentHost.endsWith(".naezipscan.com"),
      isJasanoneclickDomain:
        currentHost === "jasanoneclick.com" || currentHost.endsWith(".jasanoneclick.com"),
    };
  }
  return cachedDomainCheck;
}

router.beforeEach((to, from, next) => {
  lastRoutePath = from.fullPath;

  // KRE-76: /map(미어캣)은 이 앱 화면이 아니라 같은 도메인의 다른 앱이다(vercel.json rewrite).
  // 로그인 뒤 복귀(return=/map/…) 등 앱 안 이동으로 오면 전체 페이지로 연다.
  // 끝 슬래시(/map/, /map/…/)는 떼고 연다 — 슬래시 주소가 이 앱으로 오면 같은 주소를 다시 열어 무한 새로고침이 됐다(10-31).
  // 이미 그 주소에 있는데도 이 앱이 떴다면(연결 누락) 다시 열지 않고 첫 화면으로 보낸다.
  if (to.path === "/map" || to.path.startsWith("/map/")) {
    const target = to.fullPath.replace(/\/+(?=[?#]|$)/, "") || "/map";
    if (window.location.pathname + window.location.search === target) {
      next("/");
      return;
    }
    next(false);
    window.location.href = target;
    return;
  }

  // NZSC-4245: 로그인이 끊겨(만료·비로그인) 로그인 연장이 거절된 화면이 오류 화면으로 가려 하면 로그인 화면으로
  // 돌린다 — 로그인 뒤 그 화면(from)으로 되돌아온다. 로그인 없이 쓰는 화면은 오류로 가지 않으므로 영향이 없다.
  // 셜록(jasanoneclick.com)은 자체 로그인 흐름이 있어 이 판단을 하지 않는다.
  const onSherlockDomain = /(^|\.)jasanoneclick\.com$/.test(window.location.hostname);
  const authLost = onSherlockDomain
    ? null
    : authLostGuardDecision(to, from, {
        apiRecord: peekApiFailure(),
        hasToken: !!getAuthAccessToken(),
      });
  if (authLost && authLost.abort) {
    next(false);
    return;
  }
  if (authLost && authLost.login) {
    const login = authLost.login;
    clearAuthFailure();
    if (!authLost.repeat) noteLoginRedirect(Date.now(), login);
    if (authLost.fullLoad) {
      // 이 페이지에서 만료 정리가 일어났다 — 앱 안 이동은 메모리의 이전 회원 상태를 저장소에 다시 쓰게 되므로
      // 정리(로그아웃 요청)가 끝난 뒤 저장소를 한 번 더 비우고 로그인 화면을 새로 연다.
      next(false);
      goToLoginAfterExpiryCleanup(loginHref(login), undefined, () => {
        // 정상 이동이면 아래 NZSC-3077 블록이 하는 이탈 기록을, 풀 로드 전에 직접 남기고 보낸다(NZSC-3797)
        const tracker = getTracker();
        if (tracker) tracker.trackExit();
        flushBehaviorNow();
      });
    } else {
      // 처음부터 비로그인 — 메모리에 회원 상태가 없으므로 앱 안에서 이동한다
      next(login);
    }
    return;
  }

  // NZSC-4015: 오류 페이지로 가는 모든 Vue 발신(98곳)은 여기를 지난다. 직전 화면(쿼리 포함)과
  // URL 의 원인 코드를 남겨 web-core 오류 페이지가 참조번호와 함께 보고하게 한다.
  // 발신부(AnalyzeFreeBase 등)가 직전에 금액·주소상태를 실어 뒀으면 보존한다.
  if (to.path === "/analyze/error") {
    try {
      const prior = peekFrom();
      recordFrom({
        ...(prior || {}),
        from: from.fullPath,
        sender: "vue",
        code: (to.query && to.query.code) || (prior && prior.code) || undefined,
      });
    } catch (e) {
      /* 기록 실패가 이동을 막아선 안 된다 */
    }
  }
  if (to.path === "/report" && !reportEntryPoint) {
    reportEntryPoint = from.fullPath;
  }

  // NZSC-3077: 라우트가 실제로 바뀔 때만 exit_screen 발화 (같은 경로 next() X 케이스 제외).
  // tracker singleton에 trackExit 위임 — 페어링은 매니저가 보장.
  if (to.fullPath !== from.fullPath) {
    var tracker = getTracker();
    if (tracker) {
      try {
        tracker.trackExit();
      } catch (e) {
        // tracker 실패가 라우팅을 막으면 안 됨
      }
    }
  }

  // 도메인 체크 캐시 무효화 (매번 최신 상태 확인)
  cachedDomainCheck = null;
  const domainInfo = getDomainInfo();
  const { isNaezipscanDomain, isJasanoneclickDomain } = domainInfo;

  // jasanoneclick.com 도메인 체크 (sherlock 전용 도메인)
  if (isJasanoneclickDomain) {
    // NZSC-3098: query string(utm 등) 붙으면 to.fullPath="/analyze?utm_source=..."가 되어
    // 기존 `to.fullPath === "/analyze"` 비교 실패 → isRootLike=false → 아래 Line 310 분기로
    // 빠져 naezipscan.com 으로 redirect 되는 버그. 카카오 알림톡(utm_source=kakaotalk 필수)을
    // 비롯한 모든 마케팅 캠페인 URL 영향. → to.path 검사로 정정 (query 무관 매치).
    const isRootLike = to.path === "/" || to.path === "" || to.path === "/analyze";

    // 루트(/) 또는 루트 리다이렉트(/analyze) 접근 시 /sherlock/dashboard로 이동
    if (isRootLike) {
      next(false);
      window.location.replace("https://jasanoneclick.com/sherlock/dashboard");
      return;
    }

    // analyze/login 경로는 예외처리 (sherlock/naezipscan 모두 패스)
    if (to.path === "/analyze/login" || to.path === "/analyze/apple/login") {
      next();
      return;
    }

    // /sherlock이 아닌 경로 접근 시 naezipscan.com으로 리다이렉트
    if (!to.path.startsWith("/sherlock")) {
      next(false);
      window.location.replace(`https://naezipscan.com${to.fullPath}`);
      return;
    }

    next();
    return;
  }

  // naezipscan.com 도메인에서 /sherlock 접근 시 jasanoneclick.com으로 리다이렉트
  // analyze/login 경로는 예외처리 (sherlock/naezipscan 모두 패스)
  if (
    to.path.startsWith("/sherlock") &&
    isNaezipscanDomain &&
    to.path !== "/analyze/login" &&
    to.path !== "/analyze/apple/login"
  ) {
    const targetUrl = `https://jasanoneclick.com${to.fullPath}`;
    window.location.replace(targetUrl);
    return;
  }

  // NZSC-3481: 컷오버 라우트 인앱 진입 → 풀 페이지 로드 전환 (위 목록 주석 참조).
  // from.matched.length === 0 (초기 로드)은 제외 — 초기 로드가 Vue에 도달했다는 건
  // rewrite가 없다는 뜻(롤백 상태)이므로 Vue 원본을 그대로 렌더해야 하고,
  // 여기서 풀 로드를 또 하면 무한 새로고침 루프가 된다.
  // NZSC-3639(홈 컷오버): 판정을 from.matched → "브라우저 주소가 이미 그 경로인가"로 교체.
  // 루트(/)는 라우터 설정상 /analyze 로 redirect 되는데 redirect 는 초기 내비게이션이라
  // from.matched.length === 0 → 옛 조건에서는 가드를 건너뛰어 Vue 홈이 그대로 렌더됐다
  // (컷오버 후 / 로 들어온 유저가 구 화면을 보는 경로). 무한 새로고침 방지 의도는 그대로다:
  // 풀 로드 뒤에는 location.pathname === to.path 라 두 번째 발화가 없고, 롤백(rewrite 제거)
  // 상태에서 /analyze 직접 진입도 같은 이유로 한 번도 발화하지 않는다.
  // X-Ray 동봉 인스턴스는 /web/ 아래의 Vue 원본 화면을 시각화하는 전용 빌드다.
  // 여기서 web-core 루트 URL로 풀 리로드하면 캡처 서버 밖으로 빠져 화면이 사라진다.
  // 일반 고객 웹 빌드(플래그 미설정)는 기존 컷오버 동작을 그대로 유지한다.
  if (
    process.env.VUE_APP_XRAY_INSTANCE !== "1" &&
    (WEB_CORE_MIGRATED_ROUTES.has(to.path) || WEB_CORE_LOAN_GOV_ALIASES.has(to.path)) &&
    window.location.pathname !== to.path
  ) {
    // KRE-50: 첫 진입 중(가드가 /login 으로 돌려보낸 경우 등)에는 아직 아무 화면도 그리지 않았다.
    // 여기서 next(false) 하면 vue-router 가 주소가 "/"가 아닐 때 시작 위치("/")를 기록에 push 하고, href 이동이
    // 또 쌓여 로그인 X(뒤로가기)가 흰 화면 → 같은 가드 → 다시 로그인으로 돈다. 이동을 확정하지 않고 기록을 교체한다.
    // 단 탭의 첫 페이지(카톡 링크·새 탭 직접 진입)면 교체 후 뒤로 갈 곳이 없어 로그인 X가 먹통이 되므로 기존 이동을 유지한다.
    if (from === VueRouter.START_LOCATION && window.history.length > 1) {
      flushBehaviorNow();
      mriHandoff(to.fullPath);
      window.location.replace(to.fullPath);
      return;
    }
    next(false);
    // NZSC-3797: 페이지가 파기되기 전에 계측을 강제 전송한다. 직전에 큐잉된
    // click_action(하단 탭)·exit_screen 은 pagehide 를 기다리다 그대로 사라졌다.
    flushBehaviorNow();
    // NZSC-3867: MRI 프리뷰 안이면 web-core 브랜치 프리뷰로 넘길 수 있게 먼저 알린다(프로덕션 no-op)
    mriHandoff(to.fullPath);
    window.location.href = to.fullPath;
    return;
  }

  // 정상 진행
  next();
});
export { lastRoutePath, reportEntryPoint };
export default router;
